import Footer from "components/layout/Footer/Footer";
import Header from "components/layout/Header/Header";
import Container from "components/layout/Container/Container";

import s from "./DocumentPage.module.scss";

import { toMap } from "app/clinicInfoMap";
import { useClinicInfo } from "api/queries/useClinicInfo";
import {
  DEFAULT_LEGAL_INFO_APPOINTMENT_BODY,
  DEFAULT_LEGAL_INFO_APPOINTMENT_TITLE,
  DEFAULT_LEGAL_INFO_LEAD,
  DEFAULT_LEGAL_INFO_PATIENT_BODY,
  DEFAULT_LEGAL_INFO_PATIENT_TITLE,
  DEFAULT_LEGAL_INFO_TITLE,
  fillLegalTemplate,
  isExternalHref,
  LEGAL_INFO_APPOINTMENT_BODY_KEY,
  LEGAL_INFO_APPOINTMENT_TITLE_KEY,
  LEGAL_INFO_LEAD_KEY,
  LEGAL_INFO_LINKS_KEY,
  LEGAL_INFO_PATIENT_BODY_KEY,
  LEGAL_INFO_PATIENT_TITLE_KEY,
  LEGAL_INFO_TITLE_KEY,
  parseLegalLinks,
  parseLegalTextBlocks,
  type LegalLinkItem,
} from "app/legalInfo";

const API_BASE = ("");

function getPhones(cfg: Record<string, string | undefined>) {
  const phones: string[] = [];

  const raw = cfg["contacts.phones"];
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        parsed.forEach((x) => {
          if (typeof x === "string" && x.trim()) phones.push(x.trim());
        });
      }
    } catch {
      // ignore
    }
  }

  if (phones.length === 0 && cfg["contacts.phone"]?.trim()) {
    phones.push(cfg["contacts.phone"].trim());
  }

  if (phones.length === 0) {
    phones.push("+7 (000) 000-00-00");
  }

  return Array.from(new Set(phones));
}

function resolveLegalHref(href: string) {
  const clean = (href || "").trim();

  if (!clean) return "#";

  if (isExternalHref(clean)) return clean;

  if (clean.startsWith("/documents/download/")) {
    return `${API_BASE}${clean}`;
  }

  if (clean.startsWith("/uploads/")) {
    return `${API_BASE}${clean}`;
  }

  return clean;
}

function isBackendDocumentHref(href: string) {
  const clean = (href || "").trim();
  return clean.startsWith("/documents/download/") || clean.startsWith("/uploads/");
}

function DocLink({ item }: { item: LegalLinkItem }) {
  const resolvedHref = resolveLegalHref(item.href);
  const external = isExternalHref(resolvedHref);
  const backendDocument = isBackendDocumentHref(item.href);

  return (
    <a
      href={resolvedHref}
      download={item.download ? true : undefined}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      className={s.docLink}
    >
      <img src="/icons/icon_doc.svg" alt="" className={s.icon} />
      <span>{item.label}</span>

      {backendDocument ? <span className={s.docMeta}>PDF</span> : null}
    </a>
  );
}

export default function DocumentPage() {
  const { data, isLoading, isError } = useClinicInfo();
  const cfg = toMap(data ?? []);

  const hasDbData = !isLoading && !isError && (data?.length ?? 0) > 0;

  const phones = getPhones(cfg as Record<string, string | undefined>);
  const address = (cfg["contacts.address"] || "").trim() || "Адрес будет позже";

  const title =
    ((cfg[LEGAL_INFO_TITLE_KEY] as string | undefined) || "").trim() ||
    DEFAULT_LEGAL_INFO_TITLE;

  const lead =
    ((cfg[LEGAL_INFO_LEAD_KEY] as string | undefined) || "").trim() ||
    DEFAULT_LEGAL_INFO_LEAD;

  const patientTitle =
    ((cfg[LEGAL_INFO_PATIENT_TITLE_KEY] as string | undefined) || "").trim() ||
    DEFAULT_LEGAL_INFO_PATIENT_TITLE;

  const patientBodyRaw =
    ((cfg[LEGAL_INFO_PATIENT_BODY_KEY] as string | undefined) || "").trim() ||
    DEFAULT_LEGAL_INFO_PATIENT_BODY;

  const appointmentTitle =
    ((cfg[LEGAL_INFO_APPOINTMENT_TITLE_KEY] as string | undefined) || "").trim() ||
    DEFAULT_LEGAL_INFO_APPOINTMENT_TITLE;

  const appointmentBodyRaw =
    ((cfg[LEGAL_INFO_APPOINTMENT_BODY_KEY] as string | undefined) || "").trim() ||
    DEFAULT_LEGAL_INFO_APPOINTMENT_BODY;

  const patientBody = fillLegalTemplate(patientBodyRaw, {
    address,
    phones: phones.join(", "),
  });

  const appointmentBody = fillLegalTemplate(appointmentBodyRaw, {
    address,
    phones: phones.join(", "),
  });

  const patientBlocks = parseLegalTextBlocks(patientBody);
  const appointmentBlocks = parseLegalTextBlocks(appointmentBody);

  const links = parseLegalLinks(cfg[LEGAL_INFO_LINKS_KEY] as string | undefined);

  const usingFallback =
    !hasDbData ||
    !(
      (cfg[LEGAL_INFO_TITLE_KEY] || "").trim() &&
      (cfg[LEGAL_INFO_PATIENT_BODY_KEY] || "").trim() &&
      (cfg[LEGAL_INFO_APPOINTMENT_BODY_KEY] || "").trim()
    );

  return (
    <div className={s.pageWrap}>
      <Header />

      <main className={s.main}>
        <section className="section section--tight">
          <Container>
            <div className={s.panel}>
              <h1 className={s.h1}>{title}</h1>

              <p className={s.lead}>{lead}</p>

              {usingFallback ? (
                <div className={s.meta}>
                  Сейчас показан шаблонный контент. В админке можно задать свой текст и свой список
                  документов/ссылок.
                </div>
              ) : null}

              <div className={s.doc}>
                <section className={s.sectionCard}>
                  <h2 className={s.sectionTitle}>{patientTitle}</h2>

                  <div className={s.items}>
                    {patientBlocks.map((block, idx) =>
                      block.type === "paragraph" ? (
                        <p key={idx} className={s.item}>
                          {block.text}
                        </p>
                      ) : (
                        <ul key={idx} className={s.list}>
                          {block.items.map((item, itemIdx) => (
                            <li key={`${idx}_${itemIdx}`}>{item}</li>
                          ))}
                        </ul>
                      )
                    )}
                  </div>
                </section>

                <section className={s.sectionCard}>
                  <h2 className={s.sectionTitle}>{appointmentTitle}</h2>

                  <div className={s.items}>
                    {appointmentBlocks.map((block, idx) =>
                      block.type === "paragraph" ? (
                        <p key={idx} className={s.item}>
                          {block.text}
                        </p>
                      ) : (
                        <ul key={idx} className={s.list}>
                          {block.items.map((item, itemIdx) => (
                            <li key={`${idx}_${itemIdx}`}>{item}</li>
                          ))}
                        </ul>
                      )
                    )}
                  </div>
                </section>

                <section className={s.sectionCard}>
                  <h2 className={s.sectionTitle}>Документы и официальные ресурсы</h2>

                  <div className={s.docsGrid}>
                    {links.map((item, idx) => (
                      <DocLink key={`${item.label}_${idx}`} item={item} />
                    ))}
                  </div>
                </section>
              </div>
            </div>
          </Container>
        </section>
      </main>

      <Footer />
    </div>
  );
}
